/** Small quick-action popover for a data-grid cell value.
 *
 * Reusable across grids: the page composes the action list per column (open
 * details, filter by this value, reset the column filter, open a linked
 * resource, time-range presets, …). The wrapped cell content becomes the
 * click trigger (dotted underline); actions render as a compact icon-button
 * panel — actions without an icon render as small text buttons (e.g. "±5m"
 * range presets for timestamp columns).
 *
 * The trigger is keyboard-reachable: it is focusable, Enter/Space open the
 * panel and Escape closes it. A keyboard open always uses the modal `Popover`,
 * which moves focus into the panel and returns it on close. Pointer opens
 * follow the mode: in `auto` (hover) mode both hover and click render the
 * non-modal `Popper`, so the rest of the grid stays interactive; without
 * `auto` a click renders the modal `Popover` as it always did.
 */
import {
  Box,
  Button,
  IconButton,
  Paper,
  Popover,
  Popper,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';

export interface CellAction {
  key: string;
  /** Tooltip text for icon actions; button text for text actions. */
  label: string;
  /** Optional tooltip for text actions (icon actions always tooltip `label`). */
  tooltip?: string;
  icon?: ReactNode;
  onClick?: () => void;
  /** Renders the action as a link — external (http(s), opens a new tab) or an
   *  internal route path. Omit to make it a plain button. */
  href?: string;
  /** Open an INTERNAL `href` in a new tab. Rendered as a plain anchor rather
   *  than a RouterLink, which is exactly what the scope-aware anchor patch
   *  (`useScopeAwareAnchors`) expects — the tenant scope rides along.
   *  External hrefs always open in a new tab regardless. */
  newTab?: boolean;
  /** Leave the popover open after this action fires — for multi-click flows
   *  (e.g. picking the first bound of a range). Default false (close). */
  keepOpen?: boolean;
  /** Render the action visually selected (contained text button,
   *  aria-pressed) — e.g. the bounds/body of a range being picked. */
  active?: boolean;
  /** Pointer entered this action — lets multi-click flows preview a
   *  hovered range before the completing click. */
  onHover?: () => void;
}

interface Props {
  /** Cell content that opens the popover on click. */
  children: ReactNode;
  actions: CellAction[];
  /** Optional caption rendered inside the popover above the actions. Use this
   *  instead of a hover `title`/Tooltip on the cell value — the popover would
   *  cover/replace the tooltip anyway (e.g. the UTC timestamp on a localized
   *  time column). */
  title?: ReactNode;
  /** Extra styles for the trigger wrapper. */
  sx?: SxProps<Theme>;
  /** Also open automatically on hover, after `autoDelayMs`. A click still
   *  opens immediately. Hover mode renders a non-modal Popper (no backdrop),
   *  so the rest of the grid stays interactive. */
  auto?: boolean;
  /** Hover delay before auto-opening, ms. 0 = immediately. */
  autoDelayMs?: number;
  /** Called whenever the popover closes (action click, click-away, hover-out).
   *  Lets a multi-click flow (see CellAction.keepOpen) reset pending state. */
  onClose?: () => void;
  /** Extra attributes for the trigger: a roving-focus host (a one-tab-stop table) sets
   *  its `tabIndex` and its own `data-*` marker. */
  triggerProps?: TriggerProps;
}

export type TriggerProps = { tabIndex?: number } & { [attr: `data-${string}`]: string | undefined };

/** Grace period (ms) for the mouse to travel from trigger to panel. */
const HOVER_CLOSE_DELAY_MS = 150;

/** How an `href` action renders: a RouterLink for in-app navigation, a plain
 *  anchor for anything else — external URLs, `mailto:`/`tel:` (routing a
 *  scheme'd href through RouterLink would try to navigate the SPA to the
 *  scheme as a path), and internal hrefs the caller wants in a new tab. */
function linkKind(action: CellAction): 'router' | 'anchor' | 'anchor-new-tab' | null {
  if (!action.href) return null;
  const external = /^https?:\/\//.test(action.href);
  const scheme = /^[a-z][a-z0-9+.-]*:/i.test(action.href);
  if (external || action.newTab) return 'anchor-new-tab';
  if (scheme) return 'anchor';
  return 'router';
}

const NEW_TAB = { target: '_blank', rel: 'noopener noreferrer' } as const;

function ActionButton({ action, onDone }: { action: CellAction; onDone: () => void }) {
  const handleClick = () => {
    action.onClick?.();
    if (!action.keepOpen) onDone();
  };
  const kind = linkKind(action);
  const common = { onClick: handleClick, onMouseEnter: action.onHover };
  // The three render shapes share nothing but props; spelling them out keeps
  // the `component` generic happy without casts.
  const linkProps =
    kind === 'router'
      ? { component: RouterLink, to: action.href! }
      : kind === 'anchor'
        ? { component: 'a' as const, href: action.href! }
        : kind === 'anchor-new-tab'
          ? { component: 'a' as const, href: action.href!, ...NEW_TAB }
          : {};

  if (!action.icon) {
    const btn = (
      <Button
        size="small"
        {...common}
        {...linkProps}
        variant={action.active ? 'contained' : 'text'}
        {...(action.active !== undefined && { 'aria-pressed': action.active })}
        sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: 12, lineHeight: 1.2 }}
      >
        {action.label}
      </Button>
    );
    // `top`: the default bottom placement lands the tooltip on the panel's
    // own button row.
    return action.tooltip ? (
      <Tooltip title={action.tooltip} placement="top">
        {btn}
      </Tooltip>
    ) : (
      btn
    );
  }
  return (
    <Tooltip title={action.label} placement="top">
      <IconButton size="small" aria-label={action.label} {...common} {...linkProps}>
        {action.icon}
      </IconButton>
    </Tooltip>
  );
}

export function CellActionsPopover({
  children,
  actions,
  title,
  sx,
  auto = false,
  autoDelayMs = 300,
  onClose,
  triggerProps,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Which surface is open: the modal Popover (keyboard opens, and clicks
  // outside hover mode) or the non-modal Popper (everything in hover mode).
  const [modal, setModal] = useState(false);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const cancelTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };
  // Grid rows virtualize in and out — never leave timers running past unmount.
  useEffect(() => cancelTimers, []);
  const close = () => {
    if (anchor) onClose?.();
    setAnchor(null);
    setModal(false);
  };
  const scheduleClose = () => {
    window.clearTimeout(openTimer.current);
    closeTimer.current = window.setTimeout(close, HOVER_CLOSE_DELAY_MS);
  };
  const open = (el: HTMLElement, asModal: boolean) => {
    cancelTimers();
    setModal(asModal);
    setAnchor(el);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      open(e.currentTarget, true);
    } else if (e.key === 'Escape' && anchor) {
      e.stopPropagation();
      close();
    }
  };

  const panel = (
    <Box sx={{ p: 0.5, maxWidth: 380 }}>
      {title && (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          sx={{ px: 0.5, pb: 0.25 }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
        {actions.map((a) => (
          <ActionButton key={a.key} action={a} onDone={close} />
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        component="span"
        role="button"
        tabIndex={0}
        {...triggerProps}
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
        onClick={(e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          // Already open (from hover): keep the panel as it is rather than
          // swapping in a backdrop under the pointer.
          if (anchor) return;
          open(e.currentTarget, !auto);
        }}
        onKeyDown={onKeyDown}
        {...(auto && {
          onMouseEnter: (e: MouseEvent<HTMLElement>) => {
            const el = e.currentTarget;
            cancelTimers();
            openTimer.current = window.setTimeout(() => open(el, false), autoDelayMs);
          },
          onMouseLeave: scheduleClose,
        })}
        sx={{
          cursor: 'pointer',
          textDecoration: 'underline dotted',
          '&:hover': { textDecorationStyle: 'solid' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
            borderRadius: 0.5,
          },
          ...sx,
        }}
      >
        {children}
      </Box>
      {!modal ? (
        <Popper
          open={Boolean(anchor)}
          anchorEl={anchor}
          placement="bottom-start"
          sx={{ zIndex: (t) => t.zIndex.tooltip }}
        >
          <Paper
            elevation={4}
            onMouseEnter={cancelTimers}
            onMouseLeave={scheduleClose}
            onClick={(e) => e.stopPropagation()}
          >
            {panel}
          </Paper>
        </Popper>
      ) : (
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClick={(e) => e.stopPropagation()}
        >
          {panel}
        </Popover>
      )}
    </>
  );
}
